'use client'
import { cn } from '@/shared/lib/classnames'
import { CheckboxWithBorder, useSelectCheckbox } from '@/shared/ui/checkbox'
import { SelectCheckboxProvider } from '../context/select-checkbox-context'
import { useEffect, useId, useState } from 'react'
import { SelectCheckboxSharedProps } from './types'
import { ShowMoreButton, ShowMoreButtonProps } from '@/shared/ui/show-more'
import { Option } from '@/shared/lib/types/field-option'

export interface ComponentProps extends SelectCheckboxSharedProps {
  showMoreProps?: Partial<Omit<ShowMoreButtonProps, 'items'>>
}

function Component({
  options,
  valueFieldName = 'value',
  labelFieldName = 'label',
  idFieldName = 'id',
  className,
  onChange,
  slots,
  checkboxProps,
  values,
  showMoreProps,
}: ComponentProps) {
  const [sortedOptions, setSortedOptions] = useState<Option[]>([])
  const { onChange: handleOnChange, isChecked, setValues } = useSelectCheckbox()
  const use_id = useId()

  useEffect(() => {
    if (values && setValues) setValues(values)
    if (values === undefined && setValues) setValues([])
    if (values && setValues) setSortedOptions(getSortedOptions() || [])
    if (values === undefined && setValues) setSortedOptions([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values])

  useEffect(() => {
    if (options && setValues) setSortedOptions(getSortedOptions() || [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options])

  const getSortedOptions = () => {
    if (!isChecked) return null
    return options.sort((a, b) => {
      const aValue = isChecked(a?.[valueFieldName])
      const bValue = isChecked(b?.[valueFieldName])
      return +bValue - +aValue
    })
  }

  return (
    <div className={cn('flex flex-col gap-2 justify-start w-full', className)}>
      <ShowMoreButton
        {...showMoreProps}
        defaultVisibleItems={showMoreProps?.defaultVisibleItems || 4}
        loadItemsCount={showMoreProps?.loadItemsCount || 10000000}
        renderItem={
          showMoreProps?.renderItem ||
          function (item) {
            const label = item?.[labelFieldName]
            const value = item?.[valueFieldName]
            const id = item?.[idFieldName]
            if (!value) return null

            return (
              <CheckboxWithBorder
                {...checkboxProps}
                checked={isChecked && isChecked(value)}
                onCheckedChange={(checked) => {
                  handleOnChange &&
                    handleOnChange({
                      value,
                      isChecked: !checked,
                      onChangeCallback: (v) => onChange && onChange(v, item),
                    })
                }}
                id={(id || value) + use_id}
                label={label}
              />
            )
          }
        }
        items={sortedOptions || []}
      />
      {slots?.errorMessage && slots?.errorMessage}
    </div>
  )
}

export default function ComponentWithProvider({ defaultValues, ...rest }: ComponentProps) {
  return (
    <SelectCheckboxProvider value={{ defaultValues }}>
      <Component defaultValues={defaultValues} {...rest} />
    </SelectCheckboxProvider>
  )
}
